import type { FC } from 'react'

import { Select, Input, InputWithValidation } from '@skytech/ui'
import {
  getIsDateInRange,
  getLocaleTimezoneIsoDate,
  getMaxDate,
  getMinDate,
  getWhiteLabelForDealerStyle,
} from '@skytech/utils'
import { COUNTRY_FLAGS, countryOptions } from '@skytech/utils'
import { useDispatch, useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import Button from '@/components/form/Button'
import { Translation } from '@/components/i18n/Translation'
import Textarea from '@/components/primitives/Textarea'
import {
  getIsPostPending,
  getOriginalSubscriptionOrder,
  getSubscriptionOrder,
  saveSubscriptionOrder,
  updateOrderProp,
} from '@/ducks/subscriptionOrderReducer'
import { isOrderOfType, SUBSCRIPTION_ORDER_STATUS, SUBSCRIPTION_ORDER_TYPES } from '@/util/orders'

export const SubscriptionOrderInputs: FC = () => {
  const { whiteLabel } = useAppContext()
  const dispatch = useDispatch()
  const order = useSelector(getSubscriptionOrder)
  const originalOrder = useSelector(getOriginalSubscriptionOrder)
  const isPostPending = useSelector(getIsPostPending)
  const {
    dealer_comment,
    existing_dealer_comment,
    customer_comment,
    activation_date,
    termination_date,
    status,
    code,
    country_id,
    id,
  } = order

  const isActivation = isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.PORTING)
  const isTermination = isOrderOfType(order, SUBSCRIPTION_ORDER_TYPES.TERMINATION)
  const isWaitingForUserStatus = status === 0

  const activationDateChanged = activation_date !== originalOrder?.activation_date
  const terminationDateChanged = termination_date !== originalOrder?.termination_date

  const today = new Date()
  const originalActivationDate = originalOrder?.activation_date
    ? new Date(originalOrder.activation_date)
    : null
  const originalTerminationDate = originalOrder?.termination_date
    ? new Date(originalOrder.termination_date)
    : null

  const minActivationDate = originalActivationDate
    ? new Date(Math.min(today.getTime(), originalActivationDate.getTime()))
    : today

  const minTerminationDate = originalTerminationDate
    ? new Date(Math.min(today.getTime(), originalTerminationDate.getTime()))
    : today

  const isActivationDateValid =
    !isActivation ||
    !activationDateChanged ||
    getIsActivationDateValid(activation_date, minActivationDate)

  const isTerminationDateValid =
    !isTermination ||
    !terminationDateChanged ||
    getIsTerminationDateValid(termination_date, minTerminationDate)

  return (
    <section>
      <h3>
        <Translation id="Notes" />
      </h3>

      <form onSubmit={e => e.preventDefault()}>
        {isActivation && (
          <>
            <div>
              <InputWithValidation
                id="activation_date"
                type="date"
                value={getLocaleTimezoneIsoDate(activation_date)}
                isDisabled={isPostPending || !isOrderEditable(status)}
                label={<Translation id="Activation date" />}
                min={getLocaleTimezoneIsoDate(getMinDate(3, minActivationDate))}
                max={getLocaleTimezoneIsoDate(getMaxDate(12, minActivationDate))}
                validator={() => isActivationDateValid}
                errorMessage={
                  <Translation
                    id={
                      activation_date
                        ? 'The provided date must fall between 3 months prior to today and 1 year after today'
                        : 'This field is mandatory'
                    }
                  />
                }
                onChange={e =>
                  dispatch(updateOrderProp({ prop: 'activation_date', value: e.target.value }))
                }
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              />
            </div>

            <div className="flex w-full flex-col gap-4 md:flex-row">
              <Select
                label={<Translation id="Country code" />}
                id="country_id"
                className="basis-3/12 lg:basis-4/12"
                value={country_id}
                options={countryOptions.map(option => ({
                  value: option.value,
                  label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                }))}
                onChange={option => {
                  if (typeof option?.value !== 'number')
                    throw Error('Wrong contact number country id value')
                  dispatch(updateOrderProp({ prop: 'country_id', value: option.value }))
                }}
              />
              <Input
                isFullWidth
                id="code"
                wrapperClassName="basis-9/12 lg:basis-8/12"
                type="text"
                value={code || ''}
                isDisabled={
                  isPostPending ||
                  !isOrderEditable(status) ||
                  (status === SUBSCRIPTION_ORDER_STATUS.FAILED && !order.is_new_number)
                }
                label={<Translation id="Phone number" />}
                onChange={e => dispatch(updateOrderProp({ prop: 'code', value: e.target.value }))}
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              />
            </div>
          </>
        )}

        {isTermination && (
          <div>
            <InputWithValidation
              id="termination_date"
              type="date"
              value={getLocaleTimezoneIsoDate(termination_date)}
              isDisabled={isPostPending || !orderIsOpen(status)}
              label={<Translation id="Termination date" />}
              min={getLocaleTimezoneIsoDate(getMinDate(0, minTerminationDate))}
              max={getLocaleTimezoneIsoDate(getMaxDate(12))}
              onChange={e =>
                dispatch(updateOrderProp({ prop: 'termination_date', value: e.target.value }))
              }
              validator={() => isTerminationDateValid}
              errorMessage={
                <Translation
                  id={
                    termination_date
                      ? 'The provided date must fall between today and 1 year after today'
                      : 'This field is mandatory'
                  }
                />
              }
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            />
          </div>
        )}
        <div className="flex gap-2">
          <Textarea
            className="flex-grow"
            value={dealer_comment || ''}
            onChange={e =>
              dispatch(updateOrderProp({ prop: 'dealer_comment', value: e.target.value }))
            }
            disabled={isPostPending && !isWaitingForUserStatus}
            label={
              <>
                <Translation id="Your comment (Internal)" />
                <div>
                  (
                  <Translation id="The internal comment cannot be edited but might be extended by the value provided below." />
                  )
                </div>
              </>
            }
          />

          {existing_dealer_comment && (
            <div className="mt-12 max-w-80">
              <Translation id="Existing comment" />: <b>{existing_dealer_comment}</b>
            </div>
          )}
        </div>

        <div>
          <Textarea
            value={customer_comment || ''}
            disabled={isPostPending && !isWaitingForUserStatus}
            label={<Translation id="Comment to customer (shows in Control)" />}
            onChange={e =>
              dispatch(updateOrderProp({ prop: 'customer_comment', value: e.target.value }))
            }
          />
        </div>

        <div>
          <Button
            // @ts-ignore
            onClick={() => dispatch(saveSubscriptionOrder(id))}
            disabled={
              (isPostPending && !isWaitingForUserStatus) ||
              !isActivationDateValid ||
              !isTerminationDateValid
            }
          >
            <Translation id="Update order" />
          </Button>
        </div>
      </form>
    </section>
  )
}

export function getIsActivationDateValid(
  activationDate: string | undefined,
  min: Date = new Date()
) {
  if (!activationDate) return false
  return getIsDateInRange({
    minDate: getMinDate(3, min),
    maxDate: getMaxDate(12),
    options: {
      date: new Date(activationDate),
    },
  })
}

export function getIsTerminationDateValid(
  terminationDate: string | undefined,
  min: Date = new Date()
) {
  if (!terminationDate) return false
  return getIsDateInRange({
    minDate: min,
    maxDate: getMaxDate(12),
    options: {
      date: new Date(terminationDate),
    },
  })
}

export const orderIsOpen = (status: any) =>
  [
    SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER,
    SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_DEALER,
    SUBSCRIPTION_ORDER_STATUS.PROCESSING,
    SUBSCRIPTION_ORDER_STATUS.TERMINATION_REQUEST,
  ].includes(status)

export const isOrderEditable = (status: any) =>
  [
    SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_USER,
    SUBSCRIPTION_ORDER_STATUS.WAITING_FOR_DEALER,
    SUBSCRIPTION_ORDER_STATUS.PROCESSING,
    SUBSCRIPTION_ORDER_STATUS.TERMINATION_REQUEST,
    SUBSCRIPTION_ORDER_STATUS.FAILED,
    SUBSCRIPTION_ORDER_STATUS.FOR_MANUAL_HANDLING,
  ].includes(status)
